import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo, useCallback } from 'react';
import { BillingAPI } from 'api/billing';
import {
  AddMembersResponse,
  BillingVersion,
  ChangePlanEstimateResponse,
  ChangePlanParams,
  ChangePlanRequestPayload,
  ChangePlanResponse,
  InvoiceDetails,
  InvoiceListResponse,
  Plans,
  PortalSession,
  Subscription,
  AddonPayload,
  AddonsUsage,
  AgentEstimateWithAddonPayload,
} from 'api/billing/types';
import {
  getBillingVersion,
  getCurrentBillingCycle,
  getPlanId,
} from 'components/organisms/module-settings/billing/utils';
import { useBillingFeatureFlag } from 'hooks/use-account';
import { AccountFeature } from 'hooks/use-account-feature';
import { TranslationKey, useTranslate } from 'hooks/use-translation';

const MODULE = 'rl_billing';

export const SUBSCRIPTION_KEY = [`${MODULE}_subscription`];
export const INVOICE_DETAILS_KEY = [`${MODULE}_invoice_details`];
const INVOICE_LIST_KEY = [`${MODULE}_invoice_list`];

export const useSubscriptionQuery = (
  includeMeta = false,
  queryOptions: UseQueryOptions<
    Subscription,
    unknown,
    Subscription,
    [typeof SUBSCRIPTION_KEY, boolean]
  > = {},
): UseQueryResult<Subscription> => {
  const isBillingEnabled = useBillingFeatureFlag();

  return useQuery(
    [SUBSCRIPTION_KEY, includeMeta],
    () => BillingAPI.getSubscription(includeMeta),
    {
      ...queryOptions,
      enabled: isBillingEnabled,
    },
  );
};

export const useInvoiceDetailsQuery = (
  queryOptions: UseQueryOptions<
    InvoiceDetails,
    unknown,
    InvoiceDetails,
    typeof INVOICE_DETAILS_KEY
  > = {},
): UseQueryResult<InvoiceDetails> =>
  useQuery(INVOICE_DETAILS_KEY, () => BillingAPI.getInvoiceDetails(), {
    ...queryOptions,
  });

export const useInvoiceListQuery = (
  subscriptionId: number,
  queryOptions: UseQueryOptions<
    InvoiceListResponse,
    unknown,
    InvoiceListResponse,
    typeof INVOICE_LIST_KEY
  > = {},
): UseQueryResult<InvoiceListResponse> =>
  useQuery(INVOICE_LIST_KEY, () => BillingAPI.getInvoiceList(subscriptionId), {
    ...queryOptions,
  });

export function useRefetchSubscriptionWithoutMeta() {
  const queryClient = useQueryClient();

  return useCallback(
    (includeMeta: boolean) => {
      queryClient.refetchQueries({
        predicate: (query) => {
          const { queryKey } = query;

          return (
            queryKey[1] === includeMeta &&
            query?.queryHash?.includes(SUBSCRIPTION_KEY?.toString())
          );
        },
      });
    },
    [queryClient],
  );
}

const getChangePlanRequestPayload = ({
  plan,
  form,
  subscription,
}: ChangePlanParams): ChangePlanRequestPayload => {
  const planId = getPlanId(
    plan,
    form?.billing_cycle ?? getCurrentBillingCycle(subscription.plan.planName),
  );

  const payload: ChangePlanRequestPayload = {
    plan: {
      planId,
      quantity: form?.quantity ?? subscription.licenseQuantity,
    },
    subscriptionId: subscription.subscriptionId,
  };

  return payload;
};

const changePlanMutationFn = (changePlanParams) => {
  return BillingAPI.changePlan(getChangePlanRequestPayload(changePlanParams));
};

const downgradePlanMutationFn = (changePlanParams) => {
  return BillingAPI.changePlan({
    ...getChangePlanRequestPayload(changePlanParams),
    hasScheduledChanges: true,
  });
};

const changePlanCheckoutMutationFn = ({
  plan,
  form,
  subscription,
  isReactivation,
}: ChangePlanParams) => {
  const planId = getPlanId(
    plan,
    form?.billing_cycle ?? getCurrentBillingCycle(subscription.plan.planName),
  );

  const payload = {
    plan: {
      planId,
      quantity: form?.quantity ?? subscription.licenseQuantity,
    },
    subscriptionId: subscription.subscriptionId,
  };

  return isReactivation
    ? BillingAPI.reactivationCheckout(payload)
    : BillingAPI.changePlanCheckout(payload);
};

const changeAddonCheckoutMutationFn = ({
  count,
  addonIdentifier,
}: AddonPayload) => {
  return BillingAPI.changeAddonCheckout(count, addonIdentifier);
};

const changePlanEstimateMutationFn = ({
  plan,
  form,
  subscription,
}: ChangePlanParams) => {
  const planId = getPlanId(
    plan,
    form?.billing_cycle ?? getCurrentBillingCycle(subscription.plan.planName),
  );

  const payload = {
    plan: {
      planId,
      quantity: form?.quantity ?? subscription.licenseQuantity,
    },
    subscriptionId: subscription.subscriptionId,
  };

  return BillingAPI.changePlanEstimate(payload);
};

const updateInvoiceDetailsMutationFn = (payload: Partial<InvoiceDetails>) => {
  return BillingAPI.updateInvoiceDetails(payload);
};

export const useChangePlanMutation = (
  options: UseMutationOptions<
    ChangePlanResponse,
    unknown,
    ChangePlanParams
  > = {},
) => useMutation(changePlanMutationFn, options);

export const useDowngradePlanMutation = (
  options: UseMutationOptions<
    ChangePlanResponse,
    unknown,
    ChangePlanParams
  > = {},
) => useMutation(downgradePlanMutationFn, options);

export const useChangePlanEstimateMutation = (
  options: UseMutationOptions<
    ChangePlanEstimateResponse,
    unknown,
    ChangePlanParams
  > = {},
) => useMutation(changePlanEstimateMutationFn, options);

export const useChangePlanCheckoutMutation = (
  options: UseMutationOptions<
    ChangePlanResponse,
    unknown,
    ChangePlanParams
  > = {},
) => useMutation(changePlanCheckoutMutationFn, options);

export const useChangeAddonCheckoutMutation = (
  options: UseMutationOptions<ChangePlanResponse, unknown, AddonPayload> = {},
) => useMutation(changeAddonCheckoutMutationFn, options);

const addonUsageDetailsMutationFn = ({
  feature,
}: {
  feature: AccountFeature;
}) => {
  return BillingAPI.getAddonUsageDetails({ feature });
};

export const useGetAddonUsageDetails = (
  options: UseMutationOptions<
    AddonsUsage,
    unknown,
    {
      feature: AccountFeature;
    }
  > = {},
) => useMutation(addonUsageDetailsMutationFn, options);

export const useCreatePortalSessionMutation = (
  options: UseMutationOptions<PortalSession, unknown, void> = {},
) => useMutation(() => BillingAPI.createPortalSession(), options);

export const useRemoveScheduledPlanChangeMutation = (
  options: UseMutationOptions<void, unknown, void> = {},
) => useMutation(() => BillingAPI.removeScheduledPlanChange(), options);

export const useScheduleSubscriptionCancellationMutation = (
  options: UseMutationOptions<void, unknown, void> = {},
) =>
  useMutation(
    (formValues) => BillingAPI.scheduleSubscriptionCancellation(formValues),
    options,
  );

export const useRemoveScheduledCancellationMutation = (
  options: UseMutationOptions<void, unknown, void> = {},
) => useMutation(() => BillingAPI.removeScheduledCancellation(), options);

export const useUpdateInvoiceDetailsMutation = (
  options: UseMutationOptions<
    InvoiceDetails,
    unknown,
    Partial<InvoiceDetails>
  > = {},
) => useMutation(updateInvoiceDetailsMutationFn, options);

const addLicensesMutationFn = (count: number) => {
  return BillingAPI.addLicences(count);
};

const addAddonLicensesMutationFn = ({ count, addonIdentifier }) => {
  return BillingAPI.addLicenceAddon(count, addonIdentifier);
};

const addAddonLicensesEstimateMutationFn = ({
  count,
  addonIdentifier,
}: AddonPayload) => {
  return BillingAPI.addLicenceAddonEstimate(count, addonIdentifier);
};

const addLicensesEstimateMutationFn = (count: number) => {
  return BillingAPI.addLicencesEstimate(count);
};

export const useAddAddonLicensesMutation = (
  options: UseMutationOptions<Subscription, unknown, AddonPayload> = {},
) => useMutation(addAddonLicensesMutationFn, options);

export const useAddLicensesMutation = (
  options: UseMutationOptions<Subscription, unknown, number> = {},
) => useMutation(addLicensesMutationFn, options);

export const useAddLicensesEstimateMutation = (
  options: UseMutationOptions<ChangePlanEstimateResponse, unknown, number> = {},
) => useMutation(addLicensesEstimateMutationFn, options);

export const useAddAddonLicensesEstimateMutation = (
  options: UseMutationOptions<
    ChangePlanEstimateResponse,
    unknown,
    AddonPayload
  > = {},
) => useMutation(addAddonLicensesEstimateMutationFn, options);

const forceSyncSubscriptionMutationFn = (subscriptionId: number) => {
  return BillingAPI.forceSyncSubscription(subscriptionId);
};

export const useForceSyncSubscriptionMutation = (
  options: UseMutationOptions<void, unknown, number> = {},
) => useMutation(forceSyncSubscriptionMutationFn, options);

const addMembersMutationFn = (payload: AgentEstimateWithAddonPayload) => {
  return BillingAPI.addMembers(payload);
};

export const useAddMembersMutation = (
  options: UseMutationOptions<
    AddMembersResponse,
    unknown,
    AgentEstimateWithAddonPayload
  > = {},
) => useMutation(addMembersMutationFn, options);

const addMemberAddonMutationFn = ({
  data,
  addonIdentifier,
}: AgentEstimateWithAddonPayload) => {
  return BillingAPI.addMembersAddon(data, addonIdentifier);
};

export const useAddMemberAddonMutuation = (
  options: UseMutationOptions<
    AddMembersResponse,
    unknown,
    AgentEstimateWithAddonPayload
  > = {},
): UseMutationResult<
  AddMembersResponse,
  unknown,
  AgentEstimateWithAddonPayload
> => useMutation(addMemberAddonMutationFn, options);

const addMemberAddonEstMutationFn = ({
  data,
  addonIdentifier,
}: AgentEstimateWithAddonPayload) => {
  return BillingAPI.estimateAddMembersAddon(data, addonIdentifier);
};

export const useAddMemberAddonEstMutuation = (
  options: UseMutationOptions<
    ChangePlanEstimateResponse,
    unknown,
    AgentEstimateWithAddonPayload
  > = {},
) => useMutation(addMemberAddonEstMutationFn, options);

const addMembersEstimateMutationFn = (
  payload: AgentEstimateWithAddonPayload,
) => {
  return BillingAPI.addMembersEstimate(payload);
};

export type MembersEstimateMutationType = (
  options?: UseMutationOptions<
    ChangePlanEstimateResponse,
    unknown,
    AgentEstimateWithAddonPayload
  >,
) => UseMutationResult<
  ChangePlanEstimateResponse,
  unknown,
  AgentEstimateWithAddonPayload
>;

export const useAddMembersEstimateMutation = (
  options: UseMutationOptions<
    ChangePlanEstimateResponse,
    unknown,
    AgentEstimateWithAddonPayload
  > = {},
): UseMutationResult<
  ChangePlanEstimateResponse,
  unknown,
  AgentEstimateWithAddonPayload
> => useMutation(addMembersEstimateMutationFn, options);

export const useAddPartnerMembersEstimateMutation = (
  options: UseMutationOptions<
    ChangePlanEstimateResponse,
    unknown,
    AgentEstimateWithAddonPayload
  > = {},
): UseMutationResult<
  ChangePlanEstimateResponse,
  unknown,
  AgentEstimateWithAddonPayload
> =>
  useMutation(
    (payload: AgentEstimateWithAddonPayload) =>
      BillingAPI.addPartnerMembersEstimate(payload),
    options,
  );

export type ReactivateMemberMutationType = (
  options?: UseMutationOptions<ChangePlanEstimateResponse, unknown, number>,
) => UseMutationResult<ChangePlanEstimateResponse, unknown, number>;
const reactivateMemberMutationFn = (userId: number) => {
  return BillingAPI.reactivateMember(userId);
};

export const useReactivateMemberMutation = (
  options: UseMutationOptions<unknown, unknown, number> = {},
) => useMutation(reactivateMemberMutationFn, options);

const reactivateMemberEstimateMutationFn = (userId: number) => {
  return BillingAPI.reactivateMemberEstimate(userId);
};

export const useReactivateMemberEstimateMutation = (
  options: UseMutationOptions<ChangePlanEstimateResponse, unknown, number> = {},
): UseMutationResult<ChangePlanEstimateResponse, unknown, number> =>
  useMutation(reactivateMemberEstimateMutationFn, options);
export const useReactivatePartnerMemberEstimateMutation = (
  options: UseMutationOptions<ChangePlanEstimateResponse, unknown, number> = {},
): UseMutationResult<ChangePlanEstimateResponse, unknown, number> =>
  useMutation(
    (userId) => BillingAPI.reactivatePartnerMemberEstimate(userId),
    options,
  );

export const useAvailableLicensesQuery = () => {
  const { data: subscription } = useSubscriptionQuery();
  const availableLicenses = useMemo(
    () => (subscription ? subscription?.availableUserLicenseQuantity ?? 0 : 0),
    [subscription],
  );

  return availableLicenses;
};
export const useAvailablePartnerLicensesQuery = () => {
  const { data: subscription } = useSubscriptionQuery(true);
  // If account has partner addon, get the availablePartnerLicenses else get the userLicenses
  const availablePartnerLicenses = useMemo(
    () =>
      subscription?.subscriptionMeta?.hasPartnerAddon ?? false
        ? subscription?.availablePartnerLicenseQuantity ?? 0
        : subscription?.availableUserLicenseQuantity ?? 0,
    [subscription],
  );

  return availablePartnerLicenses;
};

export const useAvailableClientPartnerLicensesQuery = () => {
  const { data: subscription } = useSubscriptionQuery(true);
  // If account has partner addon, get the availablePartnerLicenses else get the userLicenses
  const availableExternalPartnerLicenses = useMemo(
    () =>
      subscription?.subscriptionMeta?.hasExternalPartnerAddon ?? false
        ? subscription?.availableExternalPartnerLicenseQuantity ?? 0
        : subscription?.availableUserLicenseQuantity ?? 0,
    [subscription],
  );

  return availableExternalPartnerLicenses;
};

export const useHasPartnerAddon = () => {
  const { data: subscription } = useSubscriptionQuery(true);
  const hasPartnerAddon = useMemo(
    () => subscription?.subscriptionMeta?.hasPartnerAddon ?? false,
    [subscription],
  );

  return hasPartnerAddon;
};

export const useBillingVersion = (): BillingVersion | null => {
  const { data: subscription } = useSubscriptionQuery();
  const version = useMemo(
    () => (subscription ? getBillingVersion(subscription?.plan.planId) : null),
    [subscription],
  );

  return version;
};

export const usePlanName = (plan?: Plans | null): string => {
  const translate = useTranslate();

  return useMemo(() => {
    if (!plan) {
      return '';
    }
    const planKey = `billing.plan.${plan}`.toLowerCase();

    return translate(planKey as TranslationKey);
  }, [plan, translate]);
};
